/**
 * A self-contained, exercisable example of the workspace foundation. It wires
 * three placeholder panels into the framework with an in-memory persistence and
 * preference store, so the whole system (device modes, drag/resize/collapse/
 * fullscreen, floating window, reset/save) can be driven without any real
 * feature screen. UI-W2 replaces this content registry with real features.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "../ui/button";
import { Select } from "../ui/select";
import { createPanelContentRegistry } from "./content";
import { FloatingPanel } from "./floating-panel";
import { InMemoryLayoutStore } from "./layout-store";
import { DEFAULT_PANEL_IDS } from "./presets";
import { WorkspaceStore } from "./workspace-store";
import { WorkspaceView } from "./workspace-view";
import type { WorkspaceLocale } from "./i18n";
import type { DeviceMode } from "./types";
import { InMemoryWorkspacePreferences } from "./workspace-preferences";
import "./workspace.css";

export interface WorkspaceDemoProps {
  readonly initialLocale?: WorkspaceLocale | undefined;
  readonly initialDeviceMode?: DeviceMode | undefined;
}

const demoTitles: Readonly<Record<WorkspaceLocale, Readonly<Record<string, string>>>> = {
  en: { situation: "Situation", resources: "Resources", activity: "Activity log" },
  de: { situation: "Lage", resources: "Einsatzmittel", activity: "Aktivitätsprotokoll" },
};

const demoBodies: Readonly<Record<WorkspaceLocale, Readonly<Record<string, string>>>> = {
  en: {
    situation: "Placeholder situation panel. A feature mounts real content here.",
    resources: "Placeholder resource list.",
    activity: "Placeholder activity feed.",
  },
  de: {
    situation: "Platzhalter-Lagepanel. Ein Feature bindet hier echten Inhalt ein.",
    resources: "Platzhalter-Einsatzmittelliste.",
    activity: "Platzhalter-Aktivitätsverlauf.",
  },
};

export const WorkspaceDemo = observer(function WorkspaceDemo({
  initialDeviceMode = "desktop",
  initialLocale = "en",
}: WorkspaceDemoProps): ReactNode {
  const [store] = useState(
    () =>
      new WorkspaceStore({
        role: "demo",
        locale: initialLocale,
        initialDeviceMode,
        layoutStore: new InMemoryLayoutStore(),
        preferences: new InMemoryWorkspacePreferences(initialDeviceMode),
        knownPanelIds: new Set(DEFAULT_PANEL_IDS),
      }),
  );
  const [floatingOpen, setFloatingOpen] = useState(false);

  const titles = demoTitles[store.locale];
  const bodies = demoBodies[store.locale];
  const registry = createPanelContentRegistry(
    DEFAULT_PANEL_IDS.map((id) => ({
      id,
      title: titles[id] ?? id,
      render: () => <p>{bodies[id] ?? ""}</p>,
    })),
  );

  return (
    <section aria-labelledby="workspace-demo-heading" className="thw-workspace-demo">
      <h2 id="workspace-demo-heading">{store.text("demoHeading")}</h2>
      <p>{store.text("demoIntro")}</p>
      <div className="thw-workspace-demo__controls">
        <label className="thw-workspace-demo__field">
          {store.text("deviceModeLabel")}
          <Select
            onChange={(event) => {
              store.setDeviceMode(event.target.value as DeviceMode);
            }}
            options={[
              { value: "desktop", label: store.text("deviceDesktop") },
              { value: "tablet", label: store.text("deviceTablet") },
              { value: "mobile", label: store.text("deviceMobile") },
            ]}
            value={store.deviceMode}
          />
        </label>
        <Button
          onClick={() => {
            store.setLocale(store.locale === "en" ? "de" : "en");
          }}
        >
          {store.locale === "en" ? "DE" : "EN"}
        </Button>
        <Button
          onClick={() => {
            setFloatingOpen(true);
          }}
        >
          {store.text("openFloating")}
        </Button>
      </div>

      <WorkspaceView contentRegistry={registry} store={store} />

      <FloatingPanel
        closeLabel={store.text("closeWindow")}
        moveLabel={store.text("floatingWindow")}
        onClose={() => {
          setFloatingOpen(false);
        }}
        open={floatingOpen}
        title={store.text("floatingWindow")}
      >
        <p>{store.text("emptyContent")}</p>
      </FloatingPanel>
    </section>
  );
});

export default WorkspaceDemo;
